import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Info } from "lucide-react";

// Tip che resta aperto al tap (mobile), in hover su desktop; renderizzato in
// portal per non essere tagliato dalle card con overflow hidden.
export const InfoTip = ({ text, testId, side = "top", children }) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const wrapRef = useRef(null);
  const pointer = useRef("mouse");
  const id = useId();

  useEffect(() => {
    if (!open || !wrapRef.current) return;
    const place = () => {
      const r = wrapRef.current.getBoundingClientRect();
      const small = window.innerWidth < 640;
      const width = small ? window.innerWidth - 32 : Math.min(272, window.innerWidth - 32);
      const left = small
        ? 16
        : Math.max(
            16,
            Math.min(r.left + r.width / 2 - width / 2, window.innerWidth - width - 16)
          );
      const below = side === "bottom" || r.top < 130;
      setCoords({
        left,
        width,
        top: below ? r.bottom + 10 : undefined,
        bottom: below ? undefined : window.innerHeight - r.top + 10,
        below,
      });
    };
    place();
    const onDocDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    const onScroll = () => setOpen(false);
    document.addEventListener("pointerdown", onDocDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", place);
    return () => {
      document.removeEventListener("pointerdown", onDocDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", place);
    };
  }, [open, side]);

  const hover = {
    onPointerEnter: (e) => {
      pointer.current = e.pointerType || "mouse";
      if (pointer.current === "mouse") setOpen(true);
    },
    onPointerLeave: (e) => {
      if ((e.pointerType || "mouse") === "mouse") setOpen(false);
    },
  };

  // Su desktop il click conferma l'apertura, su touch fa da interruttore.
  const toggle = () => setOpen((v) => (pointer.current === "mouse" ? true : !v));

  return (
    <span ref={wrapRef} className="relative inline-flex align-middle" {...hover}>
      {children ? (
        <span
          role="button"
          tabIndex={0}
          data-testid={testId}
          aria-describedby={open ? id : undefined}
          onClick={toggle}
          onFocus={() => setOpen(true)}
          className="cursor-help"
        >
          {children}
        </span>
      ) : (
        <button
          type="button"
          aria-label="Maggiori informazioni"
          aria-expanded={open}
          aria-describedby={open ? id : undefined}
          data-testid={testId}
          onClick={toggle}
          onFocus={() => setOpen(true)}
          className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E0708A]/60 ${
            open ? "bg-[#E0708A]/15 text-[#E0708A]" : "text-[#6B5E62]/70 hover:text-[#E0708A]"
          }`}
        >
          <Info size={13} />
        </button>
      )}

      {createPortal(
        <AnimatePresence>
          {open && coords && (
            <motion.span
              id={id}
              role="tooltip"
              data-testid={testId ? `${testId}-content` : undefined}
              initial={{ opacity: 0, y: coords.below ? -6 : 6, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: coords.below ? -4 : 4, scale: 0.97 }}
              transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
              style={{
                left: coords.left,
                top: coords.top,
                bottom: coords.bottom,
                width: coords.width,
              }}
              className="pointer-events-none fixed z-[120] rounded-xl bg-[#5E2A52] px-4 py-3 text-left text-xs font-normal normal-case leading-relaxed tracking-normal text-white shadow-[0_14px_40px_rgba(62,27,54,0.35)]"
            >
              {text}
            </motion.span>
          )}
        </AnimatePresence>,
        document.body
      )}
    </span>
  );
};
